import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { ReactTable, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { combinedLicenseData } from "modules/LocationLicenseOverview/utils";
import { formatPrice } from "utils/formatPrice";

import type { LicenseType } from "modules/LocationLicenseOverview";
import type { Licenses } from "modules/LocationLicenseOverview/components/LocationLicenseNavbar";
import type { LicenseItem } from "modules/LocationLicenseOverview/queryKeys";

type MonthlyTableProps = {
  items: LicenseItem[];
  isLoading: boolean;
  isLoadingItems: boolean;
  licenses?: Licenses;
  onLicenseRowClick: (rowData: LicenseType) => void;
  handleDeleteLicense: (id: number) => void;
};

const columnHelper = createColumnHelper<LicenseType>();

export const MonthlyTable = ({ items, onLicenseRowClick, handleDeleteLicense, isLoading, licenses, isLoadingItems }: MonthlyTableProps) => {
  const { t } = useTranslation();
  const canDeleteLicense = useCan(ACL_ACTIVITIES.Delete, ACL_RESOURCES.ExactLicense);

  const monthlyData = useMemo(() => combinedLicenseData(licenses, items, t, true), [licenses, items]);

  const handleDeleteLicenseOnClick = (e: React.MouseEvent<HTMLDivElement>, id: number) => {
    e.stopPropagation();
    handleDeleteLicense(id);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "code",
        header: t("v8_code", "Code"),
        cell: ({
          row: {
            original: { code }
          }
        }) =>
          code ? (
            <div className="license-code">{code}</div>
          ) : (
            <div className="no-license-code">
              <Icon className="link slash solid red" />
            </div>
          ),
        size: 50
      }),
      columnHelper.accessor(row => row, {
        id: "name",
        header: t("v8_name", "Name"),
        cell: ({
          row: {
            original: { description, name }
          }
        }) => <span>{description || name || ""}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "category",
        header: t("v8_role_license_service", "Role / License / Service"),
        cell: ({
          row: {
            original: { category }
          }
        }) => <span>{category}</span>,
        size: 300
      }),
      columnHelper.accessor(row => row, {
        id: "quantity",
        header: t("v8_amount", "Amount"),
        cell: ({
          row: {
            original: { quantity }
          }
        }) => <span>{quantity}</span>,
        size: 100
      }),
      columnHelper.accessor(row => row, {
        id: "price",
        header: t("v8_unit_price", "Unit Price"),
        cell: ({
          row: {
            original: { price }
          }
        }) => <span>{`€ ${formatPrice(price)}`}</span>,
        size: 100
      }),
      columnHelper.accessor(row => row, {
        id: "totalPrice",
        header: t("v8_total_price", "Total Price"),
        cell: ({
          row: {
            original: { totalPrice }
          }
        }) => <span>{`€ ${formatPrice(totalPrice)}`}</span>,
        size: 100
      }),
      columnHelper.accessor(row => row, {
        id: "actions",
        header: "",
        cell: ({
          row: {
            original: { categoryID, id }
          }
        }) =>
          canDeleteLicense && !categoryID && id ? (
            <div className="subitem-delete">
              <div>
                <Icon className="trash red" onClick={(e: React.MouseEvent<HTMLDivElement>) => handleDeleteLicenseOnClick(e, id)} />
              </div>
            </div>
          ) : null,
        size: 50
      })
    ],
    [t, canDeleteLicense]
  );

  return (
    <div className="MonthlyTable mb-25">
      <ReactTable
        isFetching={isLoading || isLoadingItems}
        columns={columns}
        data={monthlyData}
        emptyRowMessage={t("v8_no_monthly_licenses", "No monthly licenses")}
        onRowClick={onLicenseRowClick}
      />
    </div>
  );
};
